import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@divlab/divanui';

import debounce from '@Utils/debounce';
import styles from './CroppedText.module.css';

import type { FC, HTMLAttributes, PropsWithChildren } from 'react';

export interface CroppedTextProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  text: string;
}

const CroppedText: FC<PropsWithChildren<CroppedTextProps>> = (props) => {
  const { className, text, children, ...restProps } = props;
  const placeholderRef = useRef(null);
  const containerRef = useRef(null);
  const [croppedText, setCroppedText] = useState(text);
  const [calculate, setCalculate] = useState(false);

  const cropText = useCallback(() => {
    if (!placeholderRef.current) return;
    if (!containerRef.current) return;

    if (placeholderRef.current.clientHeight > containerRef.current.clientHeight) {
      setCroppedText((prevState) => {
        return `${prevState.slice(0, prevState.lastIndexOf(' '))}...`;
      });
    } else {
      setCalculate(true);
    }
  }, []);

  const handleResize = useCallback(() => {
    setCroppedText(text);
    setCalculate(false);

    cropText();
  }, [text, cropText]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', debounce(handleResize));

    return () => {
      window.removeEventListener('resize', debounce(handleResize));
    };
  }, [handleResize]);

  useEffect(() => {
    cropText();
  }, [croppedText, cropText]);

  return (
    <div {...restProps} className={cn(styles.container, className)} ref={containerRef}>
      <div className={cn(styles.text, { [styles.placeholder]: !calculate })} ref={placeholderRef}>
        {children}
        {croppedText}
      </div>
    </div>
  );
};

export default memo(CroppedText);
